import { Editor } from "@tiptap/react";
import { useEffect } from "react";
import {
  REFERENCE_MENTION_CONFIG,
  type MentionSuggestionItem,
} from "./chipOptionsConfig";

interface IUseEditorEventListeners {
  editor: Editor | null;
  suggestionItems: MentionSuggestionItem[];
  unselectAllNodes?: () => any;
  fullSize?: boolean;
  noSpaceOnInsert?: boolean;
}

const MENTION_SELECTOR = ".mention, .mention-styled";
const MENTION_NODE_TYPES = ["mention"];

const useEditorEventListeners = ({
  editor,
  suggestionItems,
  unselectAllNodes,
  fullSize,
  noSpaceOnInsert,
}: IUseEditorEventListeners) => {
  useEffect(() => {
    if (fullSize) {
      unselectAllNodes?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, fullSize]);

  useEffect(() => {
    if (editor) {
      const handleMentionClick = (event: MouseEvent) => {
        const mentionElement = (event.target as HTMLElement).closest(
          MENTION_SELECTOR
        );
        if (mentionElement) {
          const pos = editor.view.posAtDOM(mentionElement, 0);
          const node = editor.view.state.doc.nodeAt(pos);
          if (node && MENTION_NODE_TYPES.includes(node.type.name)) {
            editor.commands.setTextSelection({
              from: pos,
              to: pos + node.nodeSize,
            });
          }
        }
      };

      const handleContainerClick = (event: MouseEvent) => {
        const editorContent = editor.view.dom;
        const editorContainer = editorContent.closest(".editor-container");

        if (editorContainer && editorContent) {
          if (
            editorContainer.contains(event.target as Node) &&
            !editorContent.contains(event.target as Node)
          ) {
            editor.commands.focus();
          }
        }
      };

      const handleDragStart = (event: DragEvent) => {
        if (
          !event?.target ||
          typeof (event.target as HTMLElement).closest !== "function"
        )
          return;
        const mentionElement = (event.target as HTMLElement).closest(
          MENTION_SELECTOR
        );
        if (mentionElement) {
          mentionElement.classList.add("dragging");
          const pos = editor.view.posAtDOM(mentionElement, 0);
          const node = editor.view.state.doc.nodeAt(pos);
          if (node && MENTION_NODE_TYPES.includes(node.type.name)) {
            event.dataTransfer?.setData("text/plain", node.attrs.id);
            event.dataTransfer?.setData(
              "application/mention",
              JSON.stringify({
                id: node.attrs.id,
                pos: pos,
                nodeType: node.type.name,
              })
            );
          }
        }
      };

      const handleDragEnd = (event: DragEvent) => {
        if (!event.target) return;
        if (typeof (event.target as HTMLElement).closest !== "function") return;
        const mentionElement = (event.target as HTMLElement).closest(
          MENTION_SELECTOR
        );
        if (mentionElement) {
          mentionElement.classList.remove("dragging");
        }
      };

      const handleDragOver = (event: DragEvent) => {
        event.preventDefault();
      };

      const handleDrop = (event: DragEvent) => {
        event.preventDefault();
        const mentionData = event.dataTransfer?.getData("application/mention");
        if (mentionData) {
          const { id, pos: oldPos } = JSON.parse(mentionData);
          const dropPos = editor.view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          })?.pos;

          if (dropPos !== undefined) {
            const tr = editor.state.tr;

            try {
              tr.setMeta("addToHistory", true);

              const node = tr.doc.nodeAt(oldPos);
              if (node) {
                tr.delete(oldPos, oldPos + node.nodeSize);
                tr.insert(dropPos, node);
                editor.view.dispatch(tr);
              }
            } catch (error) {
              if (error instanceof RangeError) {
                const node = editor.state.doc.nodeAt(oldPos);
                if (node) {
                  const tr = editor.state.tr;
                  tr.setMeta("addToHistory", true);

                  tr.delete(oldPos, oldPos + node.nodeSize);

                  const endPos = tr.doc.content.size;
                  tr.insert(endPos, editor.schema.nodes.paragraph.create());
                  tr.insert(endPos + 1, node);

                  editor.view.dispatch(tr);
                }
              } else {
                throw error;
              }
            }
          }
        }
      };

      const editorContainer = editor.view.dom.closest(
        ".editor-container"
      ) as HTMLElement;

      editor.view.dom.addEventListener("click", handleMentionClick);
      editorContainer?.addEventListener("click", handleContainerClick);

      editor.view.dom.addEventListener("dragstart", handleDragStart);
      editor.view.dom.addEventListener("dragend", handleDragEnd);
      editor.view.dom.addEventListener("dragover", handleDragOver);
      editor.view.dom.addEventListener("drop", handleDrop);

      return () => {
        editor.view.dom.removeEventListener("click", handleMentionClick);
        editorContainer?.removeEventListener("click", handleContainerClick);

        editor.view.dom.removeEventListener("dragstart", handleDragStart);
        editor.view.dom.removeEventListener("dragend", handleDragEnd);
        editor.view.dom.removeEventListener("dragover", handleDragOver);
        editor.view.dom.removeEventListener("drop", handleDrop);
      };
    }
  }, [editor]);

  useEffect(() => {
    if (editor) {
      editor.setOptions({
        // @ts-ignore
        editorProps: { suggestionItems },
      });
    }
  }, [editor, suggestionItems]);

  const addCodeBlock = () => {
    if (editor) {
      const { state } = editor;
      const { from, to } = state.selection;
      const selectedText = state.doc.textBetween(from, to, " ");

      if (selectedText) {
        editor.chain().focus().setCodeBlock().run();
      } else {
        const node = state.doc.nodeAt(from);
        if (node && node.type.name === "paragraph" && node.textContent) {
          editor.chain().focus().setCodeBlock().run();
        } else {
          editor.chain().focus().setCodeBlock().insertContent(" ").run();
        }
      }
    }
  };

  const addListBlock = () => {
    if (editor) {
      const { state } = editor;
      const { from, to } = state.selection;
      const selectedText = state.doc.textBetween(from, to, " ");

      if (selectedText) {
        editor.chain().focus().wrapInList("bulletList").run();
      } else {
        const node = state.doc.nodeAt(from);
        if (node && node.type.name === "paragraph") {
          editor.chain().focus().wrapInList("paragraph").run();
        } else {
          editor.chain().focus().insertContent("<ul><li> </li></ul>").run();
        }
      }
    }
  };

  const addMention = (
    mention: string,
    prefix?: string,
    color?: string,
    sourceMeta?: {
      nodeImage?: string;
      nodeColor?: string;
      nodeName?: string;
      nodeKind?: "node" | "secret";
      // Human-readable display value for the mention chip. Without this,
      // the chip falls back to the raw `id` (e.g. a V2 `<uuid>.output`
      // ref). The runtime still resolves against `id`; `label` is purely
      // for what the user sees.
      label?: string;
    }
  ) => {
    if (editor) {
      editor
        .chain()
        .focus()
        .command(({ tr, commands }) => {
          const result = commands.insertContent({
            type: "mention",
            attrs: {
              id: mention,
              mentionSuggestionChar: "/",
              prefix: prefix || "",
              icon: prefix || "",
              color: color || "default",
              label: sourceMeta?.label || null,
              nodeImage: sourceMeta?.nodeImage || null,
              nodeColor: sourceMeta?.nodeColor || null,
              nodeName: sourceMeta?.nodeName || null,
              nodeKind: sourceMeta?.nodeKind || null,
            },
          });

          if (result && !noSpaceOnInsert) {
            commands.insertContent(" ");
          }

          tr.setMeta("addToHistory", true);

          return result;
        })
        .run();

      return true;
    }
    return false;
  };

  const addReferenceMention = (mention: string) => {
    if (editor) {
      editor
        .chain()
        .focus()
        .command(({ tr, commands }) => {
          const result = commands.insertContent({
            type: "referenceMention",
            attrs: {
              id: mention,
              mentionSuggestionChar: REFERENCE_MENTION_CONFIG.prefix || "@",
              icon: REFERENCE_MENTION_CONFIG.prefix || "",
              color: REFERENCE_MENTION_CONFIG.color,
            },
          });

          if (result && !noSpaceOnInsert) {
            commands.insertContent(" ");
          }

          tr.setMeta("addToHistory", true);

          return result;
        })
        .run();

      return true;
    }
    return false;
  };

  const toggleBold = () => {
    editor?.chain().focus().toggleBold().run();
  };

  const toggleItalic = () => {
    editor?.chain().focus().toggleItalic().run();
  };

  return {
    addCodeBlock,
    addListBlock,
    addMention,
    addReferenceMention,
    toggleBold,
    toggleItalic,
  };
};

export default useEditorEventListeners;
